import React, { lazy, Suspense, useEffect, useState } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import './styles/App.css'
import { PublicClientApplication, EventType } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { loginRequest, msalConfig } from './authConfig.js'
import axios from 'axios'
import { Configurations } from './config/config.js'
import { PermissionProvider } from 'components/permission/permissionContext'
import { fetchUserProfileSettings } from 'services/mergeDocService'
//import FormBuilderTestPage from './pages/demo/FormBuilderTestPage'
//import 'components/shared/form-builder/lookups'
// import { WorkflowDemo } from './components/shared/workflow'

const PageLayout = lazy(() => import('./components/layout/layout'))
const HomePage = lazy(() => import('./pages/home/home'))
const ErrorPage = lazy(() => import('./pages/error/error'))
const AdminDashboard = lazy(() => import('./pages/admin/adminDashboard'))
const UsersPage = lazy(() => import('./components/admin/users/users'))
const EditUsersPage = lazy(() => import('./components/admin/users/editUser'))
const CommissionerView = lazy(() => import('./pages/admin/commissionerView'))
const AssistantView = lazy(() => import('./pages/admin/assistantView'))
const UserVerification = lazy(
    () => import('./components/user/rolerequest/roleRequest')
)
const OrdersPage = lazy(() => import('./components/admin/orders/orders'))
const SMSNotificationPage = lazy(
    () => import('./components/admin/notifications/SmsNotification')
)
const TemplatesPage = lazy(
    () => import('./components/admin/templates/templates')
)
const SparsPage = lazy(() => import('./components/admin/spars/spars'))
const Settings = lazy(() => import('./pages/settings/settingsTabs'))
const Inbox = lazy(() => import('./components/user/inbox/inbox'))
const NotificationList = lazy(
    () => import('./pages/notification/notificationList')
)
const ProfilePage = lazy(() => import('./pages/profile/profile'))
const MergeDocPage = lazy(() => import('./pages/mergedoc/mergedoc'))
const MergeDocDashboardPage = lazy(() => import('./pages/mergedoc/dashboard'))
const MergeDocDashboardDetailsPage = lazy(
    () => import('./pages/mergedoc/details')
)
const EdiDashboardPage = lazy(() => import('./pages/edi/dashboard'))
const EdiDashboardDetailsPage = lazy(() => import('./pages/edi/details'))
const LoaderProvider = lazy(() => import('./components/shared/loader'))
const AlertProvider = lazy(() => import('./components/shared/alert'))
const KnowledgeBasePage = lazy(
    () => import('./pages/knowledgebase/knowledgebase')
)
const MergeDocIndexGatePage = lazy(() => import('./pages/mergedoc/indexGate'))
const MicroFilmPage = lazy(() => import('./pages/microfilm/microFilm'))
const TaskQueue = lazy(() => import('./pages/workflow/taskQueue'))
// const Studio = React.lazy(
//     () => import('./components/shared/form-builder/studio')
// )
//const RapidFormDevelopment = lazy(() => import('./pages/demo/rapiddev'))
//const RapidTableDevelopment = lazy(() => import('./pages/demo/rapiddevtable'))
// const FormBuilderDev = lazy(() => import('./pages/demo/multiStepForm'))
// const FormBuilderOneStepDev = lazy(() => import('./pages/demo/oneStepForm'))
// const FormBuilderSimpleDev = lazy(() => import('./pages/demo/simpleForm'))
// const DemoPage = lazy(() => import('./pages/demo/demo'))
// const FormBuilderTest = lazy(() => import('./pages/demo/FormBuilderTestPage'))

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance = new PublicClientApplication(msalConfig)
await msalInstance.initialize()
await msalInstance.handleRedirectPromise()

/**
 * add the bearer token to http request
 */
axios.defaults.baseURL = Configurations.apiEndPoints.baseUrl
axios.interceptors.request.use(
    async (response) => {
        const account = msalInstance.getAllAccounts()[0]
        const msalResponse = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: account,
        })
        if (msalResponse.accessToken) {
            response.headers['Authorization'] =
                'Bearer ' + msalResponse.accessToken
        }
        return response
    },
    (err) => {
        return Promise.reject(err)
    }
)

// Default to using the first account if no account is active on page load
if (
    !msalInstance.getActiveAccount() &&
    msalInstance.getAllAccounts().length > 0
) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0])
}

// Optional - This will update account state if a user signs in from another tab or window
msalInstance.enableAccountStorageEvents()

// Listen for sign-in event and set active account
msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
        const account = event.payload.account
        msalInstance.setActiveAccount(account)
    }
})

// handle auth redired/do all initial setup for msal
msalInstance
    .handleRedirectPromise()
    .then((tokenResponse) => {
        if (!tokenResponse) {
            const accounts = msalInstance.getAllAccounts()
            if (accounts.length === 0) {
                console.log('login redirect')
                // No user signed in
                msalInstance.loginRedirect(loginRequest)
            }
        } else {
            // Do something with the tokenResponse
        }
    })
    .catch((err) => {
        // Handle error
        console.error(err)
    })

/**
 * msal-react is built on the React context API and all parts of your app that require authentication must be
 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication
 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the
 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const App = () => {
    const [preferences, setPreferences] = useState([])
    const account = msalInstance.getAllAccounts()[0]
    useEffect(() => {
        if (account?.username) {
            fetchUserProfileSettings(account.username)
                .then((res) => {
                    const { code, data } = res
                    if (code === 0) {
                        const settings = JSON.parse(data[0].settings) ?? []
                        setPreferences(settings)
                        const selectedTheme = settings.find(
                            (setting) => setting.id === 'theme'
                        )
                        document.documentElement.setAttribute(
                            'data-theme',
                            selectedTheme.value ?? 'dark'
                        )
                    }
                })
                .catch((err) => {
                    console.log('error loading the user preferences', err)
                })
        }
        // eslint-disable-next-line
    }, [])

    const router = createBrowserRouter([
        {
            path: '/',
            element: <PageLayout />,
            errorElement: <ErrorPage />,
            children: [
                {
                    path: '/',
                    element: (
                        <MergeDocIndexGatePage preferences={preferences} />
                    ),
                },
                {
                    path: '/home',
                    element: <HomePage />,
                },
                {
                    path: '/microfilm',
                    element: <MicroFilmPage />,
                },
                // {
                //     path: '/inbox',
                //     element: <TaskQueue />,
                // },
                // {
                //     path: '/form-builder-test',
                //     element: <FormBuilderTest />,
                // },
                // {
                //     path: '/multistep-form',
                //     element: <FormBuilderDev />,
                // },
                // {
                //     path: '/onestep-form',
                //     element: <FormBuilderOneStepDev />,
                // },
                // {
                //     path: '/simple-form',
                //     element: <FormBuilderSimpleDev />,
                // },
                // {
                //     path: '/demo',
                //     element: <DemoPage />,
                // },
                // {
                //     path: '/studio',
                //     element: <Studio initialFormType="multiStep" />,
                // },
                // {
                //     path: '/workflow',
                //     element: <WorkflowDemo />,
                // },
                // {
                //     path: '/formdemo',
                //     element: <RapidFormDevelopment />,
                // },
                // {
                //     path: '/tabledemo',
                //     element: <RapidTableDevelopment />,
                // },
                {
                    path: '/profile',
                    element: <ProfilePage />,
                },
                {
                    path: '/mergedoc',
                    children: [
                        {
                            index: true,
                            element: (
                                <MergeDocIndexGatePage
                                    preferences={preferences}
                                />
                            ),
                        },
                        {
                            path: 'dashboard',
                            children: [
                                {
                                    index: true,
                                    element: <MergeDocDashboardPage />,
                                },
                                {
                                    path: 'details',
                                    element: <MergeDocDashboardDetailsPage />,
                                },
                            ],
                        },
                        {
                            path: 'editor',
                            element: <MergeDocPage preferences={preferences} />,
                        },
                    ],
                },
                {
                    path: '/edi',
                    children: [
                        {
                            path: 'dashboard',
                            children: [
                                {
                                    index: true,
                                    element: <EdiDashboardPage />,
                                },
                                {
                                    path: 'details',
                                    element: <EdiDashboardDetailsPage />,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: '/knowledgecenter',
                    element: <KnowledgeBasePage />,
                },
                {
                    path: '/notifications',
                    element: <NotificationList />,
                },
                {
                    path: '/inbox',
                    element: <Inbox />,
                },
                {
                    path: '/user/verification',
                    element: <UserVerification />,
                },
                {
                    path: '/admin',
                    children: [
                        {
                            index: true, // matches exactly "/admin"
                            element: <AdminDashboard />,
                        },
                        {
                            path: 'users',
                            element: <UsersPage />,
                        },
                        {
                            path: 'users/:id',
                            element: <EditUsersPage />,
                        },
                        {
                            path: 'orders',
                            element: <OrdersPage />,
                        },
                        {
                            path: 'templates',
                            element: <TemplatesPage />,
                        },
                        {
                            path: 'spars',
                            element: <SparsPage />,
                        },
                        {
                            path: 'notifications',
                            element: <SMSNotificationPage />,
                        },
                    ],
                },
                {
                    path: '/commissionerView',
                    element: <CommissionerView />,
                },
                {
                    path: '/assistantView',
                    element: <AssistantView />,
                },
                {
                    path: '/settings',
                    element: (
                        <Settings
                            preferences={preferences}
                            setPreferences={setPreferences}
                        />
                    ),
                },
            ],
        },
    ])

    return (
        <Suspense fallback={<div>Loading...</div>}>
            <LoaderProvider>
                <AlertProvider>
                    <MsalProvider instance={msalInstance}>
                        {msalInstance.getActiveAccount()?.username ? (
                            <PermissionProvider
                                userEmail={
                                    msalInstance.getActiveAccount().username
                                }
                            >
                                <RouterProvider
                                    router={router}
                                    future={{
                                        v7_startTransition: true,
                                        v7_relativeSplatPath: true,
                                    }}
                                />
                            </PermissionProvider>
                        ) : (
                            <div>Initializing user session...</div>
                        )}
                    </MsalProvider>
                </AlertProvider>
            </LoaderProvider>
        </Suspense>
    )
}

export default App
